import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  adminGetPerformers, adminGetStats, getGenres, adminDeletePerformer,
  adminCreateEvent, adminAddEventPerformer, adminGetEvents,
  adminGetVenues, adminGetMcs,
  adminGetInstrumentTypes, adminCreatePerformer,
  adminContactPerformer,
} from '../services/api';
import Navbar from '../components/Navbar';
import AdminTabBar from '../components/AdminTabBar';
import EventModal from '../components/EventModal';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'new',          label: 'New'          },
  { value: 'active',       label: 'Active'       },
  { value: 'contacted',    label: 'Contacted'    },
  { value: 'booked',       label: 'Booked'       },
  { value: 'not_suitable', label: 'Not Suitable' },
  { value: 'inactive',     label: 'Inactive'     },
];

const PERF_TYPES = [
  { value: '', label: 'All types' },
  { value: 'Solo',    label: 'Solo'    },
  { value: 'Duo',     label: 'Duo'     },
  { value: 'Trio',    label: 'Trio'    },
  { value: 'Band',    label: 'Band'    },
  { value: 'DJ',      label: 'DJ'      },
  { value: 'Other',   label: 'Other'   },
];

const ADD_PERF_TYPES = ['Solo', 'Duo', 'Trio', 'Band', 'DJ', 'Spoken word', 'Other'];
const CONNECTION_OPTIONS = ['DI', 'Amplifier', 'Acoustic', 'Electric', 'Other'];

const EMPTY_ADD_FORM = {
  real_name: '', stage_name: '', email: '', password: '',
  performance_type: '', performers_on_stage: '',
  vocal_mics: '0', instruments: [],
};

/**
 * Send a one-off email to a single performer.
 *
 * The recipient is taken from the row rather than re-fetched — the list already
 * carries the name and address, and showing them before sending is the point.
 * Posts to the existing /performers/:id/contact endpoint, which also logs the
 * message as an admin note and moves a "new" performer to "contacted".
 */
function EmailPerformerControl({ performer }) {
  const [open,    setOpen]    = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [sent,    setSent]    = useState(null);
  const [subject, setSubject] = useState('');
  const [bodyTxt, setBodyTxt] = useState('');

  const name  = performer.stage_name || performer.real_name || performer.email;
  const first = (performer.real_name || performer.stage_name || '').trim().split(/\s+/)[0] || 'there';

  function openDialog() {
    setError(''); setSent(null);
    setSubject('SCALA Performer Registry');
    setBodyTxt(`Hi ${first},\n\n\n\n— SCALA`);
    setOpen(true);
  }

  async function send() {
    setBusy(true);
    setError('');
    try {
      const res = await adminContactPerformer(performer.performer_id, {
        subject,
        body: bodyTxt,
      });
      setSent(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'The email did not send.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        className="a-btn a-btn--sm"
        // Same blue family as the app's other comms actions, several shades back,
        // so a one-off email is never mistaken for a booking action at a glance.
        // Matches the Email Entrant button in FOOM.
        style={{ background: '#bae6fd', color: '#075985', border: '1px solid #7dd3fc' }}
        onClick={openDialog}
        title={`Email ${name}`}
      >
        Email Performer
      </button>

      {open && (
        <div
          className="a-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}
        >
          <div className="a-modal" style={{ maxWidth: '620px', width: '100%' }}>
            <h2 className="a-modal__title">Email performer</h2>

            {sent ? (
              <>
                <p style={{ margin: '0 0 1.25rem', lineHeight: 1.5 }}>
                  Sent to <strong>{sent.name || name}</strong> ({sent.email || performer.email}).
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="a-btn a-btn--navy" onClick={() => setOpen(false)}>Close</button>
                </div>
              </>
            ) : (
              <>
                {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

                <div style={{
                  background: 'var(--surface, #f8f7f4)', border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '8px', padding: '0.6rem 0.9rem', marginBottom: '1rem',
                  fontSize: '0.83rem', lineHeight: 1.6,
                }}>
                  <div><span style={{ color: 'var(--muted)' }}>To:</span> <strong>{name}</strong> &lt;{performer.email}&gt;</div>
                </div>

                <div className="a-field">
                  <label className="a-label">Subject</label>
                  <input
                    className="a-input"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div className="a-field" style={{ marginBottom: '0.4rem' }}>
                  <label className="a-label">Message</label>
                  <textarea
                    className="a-input"
                    rows={11}
                    value={bodyTxt}
                    onChange={(e) => setBodyTxt(e.target.value)}
                    style={{ fontSize: '0.85rem', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                  />
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0 0 1rem' }}>
                  Sends immediately — there is no draft or undo. Blank lines become paragraph breaks.
                  The message is logged against this performer, and a performer still marked “new”
                  becomes “contacted”.
                </p>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button className="a-btn a-btn--ghost" onClick={() => setOpen(false)} disabled={busy}>
                    Cancel
                  </button>
                  <button
                    className="a-btn a-btn--navy"
                    onClick={send}
                    disabled={busy || !subject.trim() || !bodyTxt.trim()}
                  >
                    {busy ? 'Sending…' : 'Send Email'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminPerformersPage() {
  const navigate = useNavigate();

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  })();
  const isAdmin = ['admin', 'metaadmin'].includes(currentUser.role);
  const canCreate = ['admin', 'metaadmin', 'booking_manager'].includes(currentUser.role);
  // Messaging a performer you are booking is part of booking them, so booking
  // managers get this too (server: POST /performers/:id/contact is
  // bookingManagerOrAdmin). Editing a profile and deleting a performer stay on
  // isAdmin - they are not booking, and their buttons stay hidden for the role.
  const canEmail = ['admin', 'metaadmin', 'booking_manager'].includes(currentUser.role);

  const [performers, setPerformers] = useState([]);
  const [genres, setGenres]         = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [downloading,      setDownloading]      = useState(null);
  const [prefilledPerformer, setPrefilledPerformer] = useState(null);
  const [venues, setVenues]         = useState([]);
  const [mcs, setMcs]               = useState([]);
  const [bookSaving, setBookSaving] = useState(false);
  const [bookError, setBookError]   = useState('');

  // Book dialog: pick an upcoming event, or fall through to creating a new one
  const [bookingPerformer, setBookingPerformer] = useState(null);
  const [upcomingEvents,   setUpcomingEvents]   = useState([]);
  const [eventsLoading,    setEventsLoading]    = useState(false);
  const [chosenEventId,    setChosenEventId]    = useState('');
  const [addingToEvent,    setAddingToEvent]    = useState(false);
  const [bookDialogError,  setBookDialogError]  = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  // Add Performer modal state
  const [showAddModal, setShowAddModal]   = useState(false);
  const [addForm, setAddForm]             = useState(EMPTY_ADD_FORM);
  const [addSaving, setAddSaving]         = useState(false);
  const [addError, setAddError]           = useState('');
  const [showPassword, setShowPassword]   = useState(false);
  const [instrumentTypes, setInstrumentTypes] = useState([]);
  const [createdBanner, setCreatedBanner] = useState(null); // { email, password }

  const [filters, setFilters] = useState({
    search: '',
    genre: '',
    performance_type: '',
    status: '',
  });

  const [sortCol, setSortCol] = useState('registered');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback((f) => {
    setLoading(true);
    adminGetPerformers(f)
      .then(res => setPerformers(res.data))
      .catch(() => setError('Failed to load performers.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(filters);
    getGenres().then(res => setGenres(res.data)).catch(() => {});
    adminGetStats().then(res => setStats(res.data)).catch(() => {});
    adminGetVenues().then(r => setVenues(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminGetMcs().then(r => setMcs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminGetInstrumentTypes().then(r => setInstrumentTypes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load(filters);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminDeletePerformer(deleteConfirm.id);
      setPerformers(prev => prev.filter(p => p.performer_id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch {
      alert('Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Booking usually means adding someone to an event that already exists, so the
  // dialog leads with those and keeps "New Event" as the last resort rather than
  // the only option.
  const openBookDialog = async (p) => {
    setBookingPerformer(p);
    setChosenEventId('');
    setBookDialogError('');
    setEventsLoading(true);
    try {
      const res = await adminGetEvents({ sort: 'asc' });
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = (res.data || []).filter((ev) => {
        if (!ev.event_date) return false;
        const d = new Date(ev.event_date);
        d.setHours(0, 0, 0, 0);
        return d >= today;
      });
      setUpcomingEvents(upcoming);
      if (upcoming.length > 0) setChosenEventId(String(upcoming[0].id));
      else setChosenEventId('new');
    } catch {
      setBookDialogError('Could not load events. You can still create a new one.');
      setUpcomingEvents([]);
      setChosenEventId('new');
    } finally {
      setEventsLoading(false);
    }
  };

  const handleBookConfirm = async () => {
    if (!bookingPerformer) return;
    if (chosenEventId === 'new') {
      const p = bookingPerformer;
      setBookingPerformer(null);
      setBookError('');
      setPrefilledPerformer(p);
      return;
    }
    setAddingToEvent(true);
    setBookDialogError('');
    try {
      await adminAddEventPerformer(chosenEventId, { performer_id: bookingPerformer.performer_id });
      setBookingPerformer(null);
      navigate(`/admin/events/${chosenEventId}`);
    } catch (err) {
      setBookDialogError(err?.response?.data?.error || 'Could not add them to that event.');
    } finally {
      setAddingToEvent(false);
    }
  };

  const handleBookCreate = async (form) => {
    setBookError('');
    setBookSaving(true);
    try {
      const res = await adminCreateEvent({
        ...form,
        venue_id:          form.venue_id ? parseInt(form.venue_id) : null,
        mc_id:             form.mc_id ? parseInt(form.mc_id) : null,
        lyricists_count:   parseInt(form.lyricists_count) || 3,
        lyrics_sets_count: parseInt(form.lyrics_sets_count) || 3,
      });
      const newEvent = res.data;
      await adminAddEventPerformer(newEvent.id, { performer_id: prefilledPerformer.performer_id });
      setPrefilledPerformer(null);
      navigate(`/admin/events/${newEvent.id}`);
    } catch (err) {
      setBookError(err?.response?.data?.error || 'Failed to create event.');
    } finally {
      setBookSaving(false);
    }
  };

  const handleAddPerformer = async (e) => {
    e.preventDefault();
    setAddError('');
    if (!addForm.real_name.trim()) { setAddError('Real name is required.'); return; }
    if (!addForm.email.trim())     { setAddError('Email is required.'); return; }
    if (!addForm.password)         { setAddError('Password is required.'); return; }
    if (addForm.password.length < 8) { setAddError('Password must be at least 8 characters.'); return; }

    setAddSaving(true);
    try {
      await adminCreatePerformer({
        real_name:           addForm.real_name.trim(),
        stage_name:          addForm.stage_name.trim() || undefined,
        email:               addForm.email.trim(),
        password:            addForm.password,
        performance_type:    addForm.performance_type || undefined,
        performers_on_stage: addForm.performers_on_stage ? parseInt(addForm.performers_on_stage) : undefined,
        vocal_mics:          parseInt(addForm.vocal_mics) || 0,
        instruments:         addForm.instruments.filter(i => i.instrument),
      });
      const savedEmail    = addForm.email.trim();
      const savedPassword = addForm.password;
      setShowAddModal(false);
      setAddForm(EMPTY_ADD_FORM);
      setShowPassword(false);
      setCreatedBanner({ email: savedEmail, password: savedPassword });
      load(filters);
    } catch (err) {
      setAddError(err?.response?.data?.error || 'Failed to create performer.');
    } finally {
      setAddSaving(false);
    }
  };

  const addInstrument = () =>
    setAddForm(f => ({ ...f, instruments: [...f.instruments, { instrument: '', connection: 'DI' }] }));

  const removeInstrument = (i) =>
    setAddForm(f => ({ ...f, instruments: f.instruments.filter((_, idx) => idx !== i) }));

  const updateInstrument = (i, field, value) =>
    setAddForm(f => ({
      ...f,
      instruments: f.instruments.map((item, idx) => idx === i ? { ...item, [field]: value } : item),
    }));

  const handleSort = (col) => {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'registered' ? 'desc' : 'asc');
    }
  };

  const sorted = [...performers].sort((a, b) => {
    let valA, valB;
    if (sortCol === 'name') {
      valA = (a.stage_name || a.real_name || '').toLowerCase();
      valB = (b.stage_name || b.real_name || '').toLowerCase();
    } else if (sortCol === 'type') {
      valA = (a.performance_type || '').toLowerCase();
      valB = (b.performance_type || '').toLowerCase();
    } else if (sortCol === 'registered') {
      valA = new Date(a.created_at);
      valB = new Date(b.created_at);
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIndicator = ({ col }) => col === sortCol
    ? <span style={{ color: '#0ea5e9', marginLeft: '4px' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
    : <span style={{ color: '#9ca3af', marginLeft: '4px' }}>⇅</span>;

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Performers</h1>
          {canCreate && (
            <button
              className="a-btn a-btn--navy a-btn--sm"
              onClick={() => { setAddError(''); setAddForm(EMPTY_ADD_FORM); setShowPassword(false); setShowAddModal(true); }}
            >
              + Add Performer
            </button>
          )}
        </div>

        {/* Created performer banner */}
        {createdBanner && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px',
            padding: '1rem 1.25rem', marginBottom: '1.25rem', fontSize: '0.875rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem',
          }}>
            <div>
              <div style={{ fontWeight: 600, color: '#166534', marginBottom: '0.4rem' }}>
                Performer created. Send them these login details:
              </div>
              <div style={{ color: '#15803d', lineHeight: 1.7, fontFamily: 'monospace' }}>
                {/* Whatever address this admin is already using is the address
                    to pass on. This was hardcoded to https://performer.ngrok-free.app,
                    an ngrok dev tunnel that is not in ngrok.yml and bypasses
                    nginx entirely - so every manually created performer was sent
                    to a tunnel that can lapse at any time, rather than to
                    performer.scala.org.au. */}
                URL: {window.location.origin}<br />
                Email: {createdBanner.email}<br />
                Password: {createdBanner.password}
              </div>
            </div>
            <button
              onClick={() => setCreatedBanner(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: '1.1rem', padding: '0 0.25rem', flexShrink: 0 }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Total', value: stats.total || 0 },
              { label: 'New',   value: stats.new   || 0 },
              { label: 'Active', value: stats.active || 0 },
              { label: 'Contacted', value: stats.contacted || 0 },
              { label: 'Booked', value: stats.booked || 0 },
            ].map(s => (
              <div key={s.label} className="a-card" style={{ textAlign: 'center', padding: '0.875rem' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)' }}>{s.value}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="a-card" style={{ marginBottom: '1.25rem', padding: '1rem' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '2 1 200px' }}>
              <label className="a-label">Search</label>
              <input
                className="a-input"
                placeholder="Name, stage name, email…"
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label className="a-label">Genre</label>
              <select className="a-select" value={filters.genre} onChange={e => handleFilter('genre', e.target.value)}>
                <option value="">All genres</option>
                {genres.map(g => <option key={g.genre_id} value={g.genre_id}>{g.name}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <label className="a-label">Type</label>
              <select className="a-select" value={filters.performance_type} onChange={e => handleFilter('performance_type', e.target.value)}>
                {PERF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 130px' }}>
              <label className="a-label">Status</label>
              <select className="a-select" value={filters.status} onChange={e => handleFilter('status', e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <button type="submit" className="a-btn a-btn--navy a-btn--sm" style={{ marginBottom: '1px' }}>Search</button>
          </form>
        </div>

        {error && <div className="a-error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem' }}>Loading…</div>
        ) : performers.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem' }}>No performers found.</div>
        ) : (
          <div className="a-card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="a-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('name')}
                    style={{ cursor: 'pointer', userSelect: 'none', color: sortCol === 'name' ? '#374151' : undefined }}
                  >
                    Name<SortIndicator col="name" />
                  </th>
                  <th
                    onClick={() => handleSort('type')}
                    style={{ cursor: 'pointer', userSelect: 'none', color: sortCol === 'type' ? '#374151' : undefined }}
                  >
                    Type<SortIndicator col="type" />
                  </th>
                  <th>Status</th>
                  <th
                    onClick={() => handleSort('registered')}
                    style={{ cursor: 'pointer', userSelect: 'none', color: sortCol === 'registered' ? '#374151' : undefined }}
                  >
                    Registered<SortIndicator col="registered" />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(p => (
                  <tr key={p.performer_id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.stage_name || p.real_name || '—'}</div>
                      {p.stage_name && p.real_name && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{p.real_name}</div>
                      )}
                      <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{p.email}</div>
                    </td>
                    <td>{p.performance_type || '—'}</td>
                    <td>
                      <span className={`a-badge a-badge--${p.status}`}>{p.status}</span>
                    </td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>{formatDate(p.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {canEmail && p.email && <EmailPerformerControl performer={p} />}
                        <Link to={`/admin/performers/${p.performer_id}`} className="a-btn a-btn--ghost a-btn--sm">
                          View
                        </Link>
                        {isAdmin && (
                          <Link to={`/admin/performers/${p.performer_id}/edit`} className="a-btn a-btn--ghost a-btn--sm">
                            Edit
                          </Link>
                        )}
                        <button
                          className={p.has_upcoming_booking ? undefined : 'a-btn a-btn--ghost a-btn--sm'}
                          style={p.has_upcoming_booking
                            ? { background: '#22c55e', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }
                            : { color: 'var(--blue)', borderColor: 'var(--blue)' }}
                          onClick={() => openBookDialog(p)}
                        >
                          {p.has_upcoming_booking ? 'Booked' : 'Book'}
                        </button>
                        <button
                          className="a-btn a-btn--ghost a-btn--sm"
                          disabled={downloading === p.performer_id}
                          onClick={() => {
                            setDownloading(p.performer_id);
                            const token = localStorage.getItem('token');
                            fetch(`/api/admin/performers/${p.performer_id}/pdf`, {
                              headers: { Authorization: `Bearer ${token}` },
                            })
                              .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
                              .then(blob => {
                                const url = URL.createObjectURL(blob);
                                const a   = document.createElement('a');
                                a.href    = url;
                                a.download = `${(p.stage_name || p.real_name || 'performer').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-performer.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              })
                              .catch(() => alert('PDF download failed.'))
                              .finally(() => setDownloading(null));
                          }}
                        >
                          {downloading === p.performer_id ? '…' : 'Profile PDF'}
                        </button>
                        {isAdmin && (
                          <button
                            className="a-btn a-btn--danger a-btn--sm"
                            onClick={() => setDeleteConfirm({ id: p.performer_id, name: p.stage_name || p.real_name || p.email })}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--muted)' }}>
          {!loading && `${performers.length} performer${performers.length !== 1 ? 's' : ''}`}
        </div>

      </div>

      {/* Book performer — choose an upcoming event, or create a new one */}
      {bookingPerformer && (
        <div
          className="a-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget && !addingToEvent) setBookingPerformer(null); }}
        >
          <div className="a-modal" style={{ maxWidth: '480px', width: '100%' }}>
            <h2 className="a-modal__title">
              Book {bookingPerformer.stage_name || bookingPerformer.real_name || bookingPerformer.email}
            </h2>

            {bookDialogError && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{bookDialogError}</div>}

            {eventsLoading ? (
              <p style={{ color: 'var(--muted)', margin: '0 0 1rem' }}>Loading events…</p>
            ) : (
              <>
                <div className="a-field">
                  <label className="a-label">Event</label>
                  <select
                    className="a-input"
                    value={chosenEventId}
                    onChange={(e) => setChosenEventId(e.target.value)}
                  >
                    {upcomingEvents.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {formatDate(ev.event_date)} — {ev.name}
                        {ev.venue_name ? ` (${ev.venue_name})` : ''}
                      </option>
                    ))}
                    <option value="new">+ New Event…</option>
                  </select>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0 0 1rem' }}>
                  {upcomingEvents.length === 0
                    ? 'No upcoming events — creating a new one is the only option.'
                    : chosenEventId === 'new'
                      ? 'Opens the new event form with this performer already added.'
                      : 'Adds them to the end of that event’s running order.'}
                </p>
              </>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setBookingPerformer(null)} disabled={addingToEvent}>
                Cancel
              </button>
              <button
                className="a-btn a-btn--navy"
                onClick={handleBookConfirm}
                disabled={addingToEvent || eventsLoading || !chosenEventId}
              >
                {addingToEvent ? 'Adding…' : chosenEventId === 'new' ? 'Continue' : 'Add to Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Book performer — create event modal */}
      {prefilledPerformer && (
        <EventModal
          isEdit={false}
          event={null}
          venues={venues}
          mcs={mcs}
          onSave={handleBookCreate}
          onClose={() => setPrefilledPerformer(null)}
          saving={bookSaving}
          error={bookError}
          prefilledPerformer={prefilledPerformer}
        />
      )}

      {/* Add Performer modal */}
      {showAddModal && (
        <div className="a-modal-backdrop">
          <div className="a-modal" style={{ maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="a-modal__title">Add Performer</div>

            <form onSubmit={handleAddPerformer}>
              {/* Real Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label className="a-label">REAL NAME *</label>
                <input
                  className="a-input"
                  value={addForm.real_name}
                  onChange={e => setAddForm(f => ({ ...f, real_name: e.target.value }))}
                  placeholder="Full name"
                  autoFocus
                />
              </div>

              {/* Stage Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label className="a-label">STAGE NAME</label>
                <input
                  className="a-input"
                  value={addForm.stage_name}
                  onChange={e => setAddForm(f => ({ ...f, stage_name: e.target.value }))}
                  placeholder="Optional"
                />
              </div>

              {/* Email */}
              <div style={{ marginBottom: '1rem' }}>
                <label className="a-label">EMAIL *</label>
                <input
                  className="a-input"
                  type="email"
                  value={addForm.email}
                  onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="performer@example.com"
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '1rem' }}>
                <label className="a-label">TEMPORARY PASSWORD *</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    className="a-input"
                    type={showPassword ? 'text' : 'password'}
                    value={addForm.password}
                    onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Min 8 characters"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="a-btn a-btn--ghost a-btn--sm"
                    onClick={() => setShowPassword(v => !v)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* Performance Type + Number on stage (row) */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ flex: 2 }}>
                  <label className="a-label">PERFORMANCE TYPE</label>
                  <select
                    className="a-select"
                    value={addForm.performance_type}
                    onChange={e => setAddForm(f => ({ ...f, performance_type: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    {ADD_PERF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="a-label">NO. ON STAGE</label>
                  <input
                    className="a-input"
                    type="number"
                    min="1"
                    value={addForm.performers_on_stage}
                    onChange={e => setAddForm(f => ({ ...f, performers_on_stage: e.target.value }))}
                    placeholder="—"
                  />
                </div>
              </div>

              {/* Vocal Mics */}
              <div style={{ marginBottom: '1rem' }}>
                <label className="a-label">VOCAL MICS</label>
                <select
                  className="a-select"
                  value={addForm.vocal_mics}
                  onChange={e => setAddForm(f => ({ ...f, vocal_mics: e.target.value }))}
                >
                  {[0,1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              {/* Instruments */}
              <div style={{ marginBottom: '1rem' }}>
                <label className="a-label">INSTRUMENTS ON STAGE</label>
                {addForm.instruments.map((instr, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <select
                      className="a-select"
                      value={instr.instrument}
                      onChange={e => updateInstrument(i, 'instrument', e.target.value)}
                      style={{ flex: 2 }}
                    >
                      <option value="">Select instrument…</option>
                      {instrumentTypes.map(it => (
                        <option key={it.id} value={it.name}>{it.name}</option>
                      ))}
                    </select>
                    <select
                      className="a-select"
                      value={instr.connection}
                      onChange={e => updateInstrument(i, 'connection', e.target.value)}
                      style={{ flex: 1 }}
                    >
                      {CONNECTION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeInstrument(i)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: '1.1rem', padding: '0 0.25rem' }}
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="a-btn a-btn--ghost a-btn--sm"
                  onClick={addInstrument}
                  style={{ marginTop: '0.25rem' }}
                >
                  + Add Instrument
                </button>
              </div>

              {addError && (
                <div className="a-error" style={{ marginBottom: '1rem' }}>{addError}</div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="a-btn a-btn--ghost"
                  onClick={() => setShowAddModal(false)}
                  disabled={addSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="a-btn a-btn--navy"
                  disabled={addSaving}
                >
                  {addSaving ? 'Creating…' : 'Create Performer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="a-modal-backdrop">
          <div className="a-modal">
            <div className="a-modal__title">Delete Performer</div>
            <p style={{ marginBottom: '1.25rem', color: 'var(--text)', fontSize: '0.9rem' }}>
              Are you sure you want to permanently delete <strong>{deleteConfirm.name}</strong>?
              This will remove their profile, account, and all uploaded files. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="a-btn a-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
